import { RouteTypeEnum } from '@/types/enums'

type EntityId = number | string

type TenantRouteEntity = {
  id: EntityId
  name?: null | string
}

/**
 * Gets the human-readable label for a route type enum value.
 *
 * @param type - The route type enum value
 * @returns The formatted label string, or '—' if the type is invalid
 */
export function getRouteTypeLabel(type: RouteTypeEnum): string {
  switch (type) {
    case RouteTypeEnum.FIXED_DESTINATION:
      return 'Fixed Destinations'
    case RouteTypeEnum.FIXED_ROUTE:
      return 'Fixed Routes'
    default:
      return '—'
  }
}

const withQuery = (
  pathname: string,
  query?: Record<string, null | string | undefined>
) => {
  if (!query) {
    return pathname
  }

  const params = new URLSearchParams()

  Object.entries(query).forEach(([key, value]) => {
    if (value) {
      params.set(key, value)
    }
  })

  const search = params.toString()

  return search ? `${pathname}?${search}` : pathname
}

export const getAdministrationAdminPath = (id: EntityId) =>
  `/administration/admins/${id}`

export const getAdministrationRolePath = (id: EntityId) =>
  `/administration/roles/${id}`

export const getAdministrationTenantAdminPath = (id: EntityId) =>
  `/administration/tenant-admins/${id}`

export const getAdministrationTenantRolePath = (id: EntityId) =>
  `/administration/tenant-roles/${id}`

export const getCountryEditPath = (countryId: EntityId) =>
  `/areas/countries/${countryId}/edit-country`

export const getStateEditPath = (stateId: EntityId) =>
  `/areas/states/${stateId}/edit-state`

export const getCityEditPath = (cityId: EntityId) =>
  `/areas/cities/${cityId}/edit-city`

export const getDriverProfilePath = (id: EntityId) => `/drivers/${id}/profile`

export const getVehicleProfilePath = (id: EntityId) => `/vehicles/${id}/profile`

/** Vehicles list pre-filtered by its `brand` / `model` URL filters (model needs brand) */
export const getVehiclesListPath = (query: {
  brand?: null | string
  model?: null | string
}) => withQuery('/vehicles', query)

/** Vehicle profile's trips tab, optionally filtered by trip date (`yyyy-MM-dd`) */
export const getVehicleTripsPath = (
  id: EntityId,
  range?: { from?: null | string; to?: null | string }
) =>
  withQuery(getVehicleProfilePath(id), {
    tab: 'trips',
    trip_date_from: range?.from,
    trip_date_to: range?.to
  })

export const getRiderInfoPath = (id: EntityId, tab = 'info') =>
  withQuery(`/riders/${id}`, { tab })

export const getTenantInfoValue = (tenant?: null | TenantRouteEntity) => {
  if (!tenant?.name) {
    return null
  }

  return `${tenant.name}-${tenant.id}`
}

export const getTenantInfoPath = (
  tenant?: null | TenantRouteEntity,
  tab?: string
) => {
  const tenantInfo = getTenantInfoValue(tenant)

  if (!tenantInfo) {
    return null
  }

  return withQuery(`/tenants/${tenantInfo}/info`, { tab })
}

export const getTenantEditPath = (tenant?: null | TenantRouteEntity) => {
  const tenantInfo = getTenantInfoValue(tenant)

  if (!tenantInfo) {
    return null
  }

  return `/tenants/${tenantInfo}/edit-tenant`
}

export const getTenantRoutePath = (routeId: EntityId, tab?: string) =>
  withQuery(`/routes/${routeId}`, { tab })

export const getRouteShiftPath = (
  routeId: EntityId,
  shiftId: EntityId,
  tab?: string
) => withQuery(`/routes/${routeId}/shifts/${shiftId}`, { tab })

export const getTripProfilePath = (id: EntityId, tab?: string) =>
  withQuery(`/trips/${id}/profile`, { tab })

export const getRequestProfilePath = (id: EntityId, tab?: string) =>
  withQuery(`/requests/${id}/profile`, { tab })

export const getEmergencyDetailPath = (id: EntityId) =>
  `/services/emergency/${id}`

export const getNotificationDetailPath = (id: EntityId) =>
  `/notifications/${id}`

export const getMaintenanceRouteDetailPath = (id: EntityId) =>
  `/services/maintenance/${id}`

export const getVehicleOptionEditPath = (optionId: EntityId) =>
  `/vehicles/options/${optionId}/edit`

export const getServiceCenterEditPath = (serviceCenterId: EntityId) =>
  `/vehicles/centers/service-centers/${serviceCenterId}/edit`

export const getGasStationEditPath = (gasStationId: EntityId) =>
  `/vehicles/centers/gas-stations/${gasStationId}/edit`

export const getFuelBrandEditPath = (fuelBrandId: EntityId) =>
  `/vehicles/centers/fuel-brands/${fuelBrandId}/edit`

export const getCarWashEditPath = (carWashId: EntityId) =>
  `/vehicles/centers/car-washes/${carWashId}/edit`

export const getOfficeEditPath = (officeId: EntityId) =>
  `/vehicles/centers/offices/${officeId}/edit`

export const getContractInfoPath = (contractId: EntityId, tab?: string) =>
  withQuery(`/contracts/${contractId}`, { tab })

export const RENTAL_CONTRACTS_PATH = '/rental-contracts'

export const getRentalContractPath = (contractId: EntityId) =>
  `${RENTAL_CONTRACTS_PATH}/${contractId}`

export const getRentalContractEditPath = (contractId: EntityId) =>
  `${getRentalContractPath(contractId)}/edit`

export const getAddRentalPath = (contractId: EntityId) =>
  `${getRentalContractPath(contractId)}/add-rental`

export const getCarRentalPath = (contractId: EntityId, rentalId: EntityId) =>
  `${getRentalContractPath(contractId)}/rentals/${rentalId}`

export const getCarRentalEditPath = (
  contractId: EntityId,
  rentalId: EntityId
) => `${getCarRentalPath(contractId, rentalId)}/edit`
